import React, { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import ModalShell from './ModalShell';
import { createCategoryApi, getBuckets } from '../../lib/api';

export default function CreateCategoryModal({ colors, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [bucket, setBucket] = useState('');
  const [buckets, setBuckets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [similar, setSimilar] = useState(null);

  useEffect(() => {
    getBuckets()
      .then(setBuckets)
      .catch(() => setBuckets([]));
  }, []);

  const canSubmit = !!name.trim() && !!bucket && !saving;

  const submit = async (force) => {
    if (!canSubmit && !force) return;
    setSaving(true); setError(null);
    try {
      const r = await createCategoryApi(name.trim(), force, 'Expenses', bucket);
      if (r.created && r.category) { onCreated(r.category.name); return; }
      setSimilar(r.similar);
    } catch (e) {
      setError(e?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <ModalShell colors={colors} onClose={onClose} title="New category" testId="mc-create-modal">
      <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.textMuted }}>
        Name
      </label>
      <input
        data-testid="mc-create-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Restaurants"
        maxLength={40}
        autoFocus
        className="w-full px-3 py-2 rounded-lg text-sm outline-none"
        style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
      />

      <label className="block text-[10px] font-bold uppercase tracking-wider mt-3 mb-1" style={{ color: colors.textMuted }}>
        Account type <span style={{ color: colors.error }}>*</span>
      </label>
      <select
        data-testid="mc-create-bucket"
        value={bucket}
        onChange={(e) => setBucket(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm outline-none"
        style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
      >
        <option value="">Select account type…</option>
        {buckets.map((b) => (
          <option key={b.code} value={b.code}>{b.label}</option>
        ))}
      </select>
      <p className="text-[11px] mt-1" style={{ color: colors.textMuted }}>
        Maps to QuickBooks chart of accounts. Required.
      </p>

      {error ? <p className="text-xs mt-2" style={{ color: colors.error }}>{error}</p> : null}
      {similar && similar.length > 0 ? (
        <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: colors.accentSoft }} data-testid="mc-similar">
          <div className="flex items-center gap-1.5">
            <AlertCircle size={14} style={{ color: colors.accent }} />
            <p className="text-xs font-bold" style={{ color: colors.accent }}>Similar categories</p>
          </div>
          {similar.map((s) => (
            <p key={s.name} className="text-xs mt-1" style={{ color: colors.text }}>
              • <strong>{s.name}</strong> — {s.reason}
            </p>
          ))}
        </div>
      ) : null}
      <div className="mt-3 flex gap-2">
        <button onClick={onClose} className="flex-1 py-2 rounded-lg text-sm font-semibold border"
          style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.textMuted }}
        >Cancel</button>
        {similar && similar.length > 0 ? (
          <button
            data-testid="mc-create-force"
            onClick={() => submit(true)}
            disabled={saving || !bucket}
            className="flex-1 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ backgroundColor: colors.accent, opacity: (saving || !bucket) ? 0.5 : 1 }}
          >Create anyway</button>
        ) : (
          <button
            data-testid="mc-create-submit"
            onClick={() => submit(false)}
            disabled={!canSubmit}
            className="flex-1 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ backgroundColor: colors.accent, opacity: canSubmit ? 1 : 0.5 }}
          >{saving ? '…' : 'Create'}</button>
        )}
      </div>
    </ModalShell>
  );
}
